import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { camelToSnake } from '@/utils/index';
import { decode } from '@/utils/custom-enc-dec';
import { getAuthProviderRedirectUrl } from './auth-redirect';

const DG_CALENDAR = process.env.VUE_APP_URL_DG_CALENDAR;

const baseHttpClient = axios.create({
  baseURL: DG_CALENDAR,
  headers: {
    'Content-Type': 'application/json',
  },
});

const customPost = async <T = any, D = any>(
  url: string,
  data: D | undefined,
  config: AxiosRequestConfig<D> | undefined,
): Promise<AxiosResponse<T>> => {
  const isFormData = data instanceof FormData;
  const requestData = isFormData ? data : camelToSnake(data);
  const response = await baseHttpClient({
    method: 'POST',
    url,
    data: requestData,
    ...config,
  });
  return response;
};

function customErrorHandler(error: any) {
  if (!error.response) {
    console.error('Network error', error);
    return error;
  }

  switch (error.response.status) {
    case 401: {
      window.location.href = getAuthProviderRedirectUrl();
      break;
    }
    case 403:
      console.error('Forbidden');
      break;
    case 404:
      console.error('Not found');
      break;
    case 500:
      console.error('Internal server error');
      break;
    default:
      console.error('Unknown error', error);
  }

  return error;
}

baseHttpClient.interceptors.request.use((config) => {
  const modifiedConfig = config;

  const token = localStorage.getItem('token');
  if (token) {
    modifiedConfig.headers.Authorization = `DGTK ${token}`;
  }

  // const project = localStorage.getItem('project');
  // if (project) {
  //   try {
  //     const { value } = decode(project) as any;
  //     // get the project id. Could be 'ID' or 'id'
  //     const projectId = value.ID || value.id;
  //     modifiedConfig.headers['Dg-Businessid'] = projectId;
  //     console.log('[calendarClient interceptor] Found project. Dg-Businessid set to:', projectId);
  //   } catch (e) {
  //     console.error('[calendarClient interceptor] Error decoding project:', e);
  //   }
  // } else {
  //   console.log('[calendarClient interceptor] No project found in localStorage');
  // }

  console.log('[calendarClient interceptor] Request:', {
    url: modifiedConfig.url,
    method: modifiedConfig.method,
    headers: { ...modifiedConfig.headers },
  });
  console.log('[calendarClient interceptor] Request data:', JSON.stringify(modifiedConfig.data));

  return modifiedConfig;
});

baseHttpClient.interceptors.response.use(
  (response) => {
    console.log('[calendarClient interceptor] Response from:', response.config.url, {
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error('[calendarClient interceptor] Response error from:', error.config?.url, error);
    customErrorHandler(error);
    return Promise.reject(error);
  },
);

export const httpClient = Object.assign(baseHttpClient, {
  post: customPost,
});

export const importEvents = async (calendarId: string, file: File) => {
  console.log('Importing events for calendarId:', calendarId, 'with file:', file.name);
  const formData = new FormData();
  formData.append('calendar_id', String(calendarId));
  formData.append('file', file);

  return baseHttpClient.request({
    method: 'POST',
    url: '/api/events/import',
    data: formData,
    headers: {
      'Content-Type': undefined,
    },
  });
};

export const exportCalendar = async (calendarId: string, calendarName: string) => {
  try {
    const response = await baseHttpClient.get(`/api/calendars/export/${calendarId}`, {
      responseType: 'blob',
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/calendar' }));

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${calendarName || 'calendar'}.ics`);
    document.body.appendChild(link);
    link.click();

    // Clean up
    if (link.parentNode) link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Error exporting calendar:', error);
    throw error;
  }
};
